import { z } from 'zod';

export const PER_PAGE = 25;

export const SOURCE_OPTIONS = [
    'cli',
    'batch',
    'ci_daily',
    'canary',
    'cloud',
] as const;
export const OUTCOME_OPTIONS = ['all_passed', 'has_failures', 'flaky'] as const;
export const FAMILY_OPTIONS = [
    'prompt_only',
    'pipeline_replay',
    'post_call_qa',
    'private_corpus',
    'canary',
    'mixed',
] as const;
export const EXECUTION_STATUS_OPTIONS = [
    'queued',
    'provisioning',
    'running',
    'packaging',
    'succeeded',
    'failed',
    'cancelled',
] as const;
export const EXECUTION_BACKEND_OPTIONS = [
    'exe_dev',
    'daytona',
    'vercel_sandbox',
    'modal',
    'local',
    'ci',
] as const;

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

export const voiceEvalRunsSearchSchema = z.object({
    page: z.coerce.number().optional().default(1),
    source: arrayParam(z.enum(SOURCE_OPTIONS)),
    family: arrayParam(z.enum(FAMILY_OPTIONS)),
    execution_status: arrayParam(z.enum(EXECUTION_STATUS_OPTIONS)),
    execution_backend: arrayParam(z.enum(EXECUTION_BACKEND_OPTIONS)),
    outcome: z.enum(OUTCOME_OPTIONS).optional(),
    // Shared/bookmarked URLs can carry the flag as the string
    // "true"/"false" rather than a JSON boolean.
    clean_only: z
        .preprocess(
            (value) => (typeof value === 'string' ? value === 'true' : value),
            z.boolean(),
        )
        .optional(),
    git_branch: z.string().optional(),
    git_commit: z.string().optional(),
    model: z.string().optional(),
    created_by_user_id: z.string().uuid().optional(),
    search: z.string().optional(),
    sort_by: z
        .enum(['created_at', 'finished_at'])
        .optional()
        .default('created_at')
        .catch('created_at'),
    sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type VoiceEvalRunsSearch = z.infer<typeof voiceEvalRunsSearchSchema>;
